import { graphql } from '@/gql/codegen/gql';
import { useSuspenseQuery } from '@apollo/client';
import { Outlet, createFileRoute, useParams } from '@tanstack/react-router';
import { createContext, useContext, useState } from 'react';
import { SideBar } from './-components/SideBar/index';
import { useCountStudyTime } from './-hooks/useCountStudyTime';
import styles from './index.module.css';

export const GET_USER_ATTENDANCE = graphql(/* GraphQL */ `
  query EmbaUserAttendanceForSidebar(
    $embaUserAttendanceSlug: String!
    $embaCourseLessonSlug: String
  ) {
    embaUserAttendance(
      embaUserAttendanceSlug: $embaUserAttendanceSlug
      embaCourseLessonSlug: $embaCourseLessonSlug
    ) {
      ...EmbaUserAttendanceForSidebar
    }
  }
`);

type SidebarContextType = {
  showSidebar: () => void;
  hideSidebar: () => void;
};

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

export const useSidebar = () => {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error('useSidebar must be used within a SidebarProvider');
  }
  return context;
};

export const Route = createFileRoute(
  '/_embaUserLayout/user-attendances/$userAttendanceSlug/_lessonSideBarLayout',
)({
  component: LayoutComponent,
});

function LayoutComponent() {
  const { userAttendanceSlug, lessonSlug } = useParams({ strict: false });

  const { error, data } = useSuspenseQuery(GET_USER_ATTENDANCE, {
    variables: {
      embaUserAttendanceSlug: userAttendanceSlug as string,
      embaCourseLessonSlug: lessonSlug,
    },
  });

  if (error) {
    throw error;
  }

  useCountStudyTime({
    userAttendanceSlug: userAttendanceSlug as string,
    lessonSlug,
  });

  const [isSidebarVisible, setSidebarVisible] = useState(true);

  const showSidebar = () => {
    setSidebarVisible(true);
  };

  const hideSidebar = () => {
    setSidebarVisible(false);
  };

  return (
    <SidebarContext.Provider value={{ showSidebar, hideSidebar }}>
      <div className={styles.sidebarLayout}>
        <main className={styles.main}>
          <Outlet />
        </main>
        {isSidebarVisible && (
          <SideBar embaUserAttendance={data.embaUserAttendance} />
        )}
      </div>
    </SidebarContext.Provider>
  );
}
